import { AnyObject } from '@chem-po/core'
import { type BillingHoldEntry } from '../billing'
import { PreviousVersion } from '../data'
import { type ProviderAuthentication } from '../providers'
import { NextAction } from '../utils'

// BillingHoldEntry moved to types/billing/holds.ts (shared with encounters);
// re-exported here so existing visit-side imports keep working.
export { type BillingHoldEntry } from '../billing'

interface VisitSentFax {
  sentOn: number
  sentBy: string
  sentByGroup: string
  sentToNumber: string
  notes: string
  storagePath: string
}

export type VisitType =
  | 'encounter'
  | 'consultation'
  | 'antepartum'
  | 'confirmation-of-pregnancy'
  | 'problem-visit'
  | 'postpartum'
  | 'newborn'
  | 'labor-and-delivery'

export interface PracticeVisit {
  type: VisitType
  patientId: string
  assessmentId: string | null
  assessmentName: string | null
  assessmentEdd: number | null
  startTime: number
  endTime: number
  gravida: number
  para: Array<number>
  keywords?: string
  currentPregnancy:
    | 'singleton-detected'
    | 'singleton-detected-fhr'
    | 'singleton-confirmed-ultrasound'
  history: Array<
    'none' | 'no-other-surgical-history' | 'prior-pregnancy-vaginal' | 'no-prior-pregnancy'
  >
  historyReview: 'low-risk' | 'low-risk-no-contraindication' | 'further-evaluation-needed'
  nextAction: NextAction | null
  nextActionDate: number | null
  historyDetails: string
  practiceId: string
  deliveryPlan: 'spontaneous-no-induction' | 'homebirth-pending'
  backupDeliveryPlan?: 'transfer' | 'transfer-if-symptoms'
  deliveryPlanDetails: string
  hospital: string
  riskAssessment: 'low' | 'info-required'
  patientFirst: string
  patientLast: string
  authentication: ProviderAuthentication<AnyObject>
  searchString: string
  signedPdfStoragePath: string | null
  allergyNotes?: string
  notes?: string
  previousVersions: Array<PreviousVisit>
  sentFaxes?: Array<VisitSentFax>
  billingHoldHistory?: BillingHoldEntry[]
  currentBillingHold?: BillingHoldEntry | null
}

export type PreviousVisit = PreviousVersion<PracticeVisit, 'nextActionDate' | 'nextAction'>
export const isPreviousVisit = (visit: PracticeVisit | PreviousVisit): visit is PreviousVisit =>
  typeof (visit as PracticeVisit).previousVersions === 'undefined'

export type SubmittedVisitData = {
  date: string
  startTime: string
  endTime: string
  patientId: string
  practiceId: string
  assessmentId: string | null
  assessmentName: string | null
  assessmentEdd: number | null
} & Pick<
  PracticeVisit,
  | 'gravida'
  | 'para'
  | 'type'
  | 'history'
  | 'currentPregnancy'
  | 'keywords'
  | 'deliveryPlan'
  | 'backupDeliveryPlan'
  | 'deliveryPlanDetails'
  | 'hospital'
  | 'historyDetails'
  | 'historyReview'
  | 'riskAssessment'
  | 'allergyNotes'
  | 'notes'
>

export type PracticeVisitDraft = SubmittedVisitData & {
  searchString: string
  patientFirst: string
  patientLast: string
  startTimeUnix: number
  endTimeUnix: number
}

export const isPracticeVisitDraft = (
  visit: PracticeVisit | PracticeVisitDraft,
): visit is PracticeVisitDraft => typeof (visit as PracticeVisitDraft).startTimeUnix !== 'undefined'

export type PracticeVisitDraftSortKey = 'date' | 'patientLast'

export type PracticeVisitSortKey = 'startTime' | 'endTime' | 'duration' | 'patientLast' | 'type'
